import React from "react"
import { cn } from "@/lib/utils"
import { LabTrace } from "@/components/ui/lab-trace"

interface PageHeaderProps {
  title: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  actions?: React.ReactNode
  className?: string
}

/**
 * The band at the top of every screen.
 *
 * ⭐ IT WASHES IN THE BRAND TINT, AND ONLY IN THE BRAND TINT.
 *
 * It used to be a fixed sky-blue tint with sky-blue ink — the same blue on
 * every page, under every tenant. That is invisible while the brand IS blue
 * and reads as two different products stitched together the moment a lab picks
 * Burgundy: a cold blue band sitting on a warm red app.
 *
 * (The old class names are described rather than quoted on purpose. Tailwind's
 * content scanner reads COMMENTS — it has no parser, only a regex over the
 * file — so writing a class name in prose generates a live CSS rule for it.
 * Naming the dead classes here shipped two dead rules and, worse, made
 * "that class is gone from the bundle" useless as a check.)
 *
 * The same mistake is available in a
 * subtler form — giving each MODULE its own hue and washing the band in it —
 * and it fails for the same reason. A per-module amber band under a violet
 * brand lasted exactly one screenshot on PetsBOXX.
 *
 * So: the band is the brand's. Anything that wants to say WHICH module you are
 * in says it with the icon and the title, not by repainting the surface.
 *
 * The title is in plain foreground ink rather than a tinted one, because a
 * heading set in brand colour competes with the primary action button beside
 * it — and under a pale brand it simply stops being readable.
 */
export function PageHeader({ title, description, icon, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        "-mx-4 md:-mx-6 -mt-4 md:-mt-6 mb-6 px-4 md:px-6 py-4",
        "bg-gradient-to-r from-primary-100 via-card to-card border-b border-border",
        className
      )}
    >
      <div className="flex items-center gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            {icon && (
              /* The icon chip is the one place the brand tint becomes a solid
                 shape. It is what carries "which module" without repainting
                 the whole band. */
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 [&>svg]:h-5 [&>svg]:w-5">
                {icon}
              </span>
            )}
            <span className="truncate">{title}</span>
          </h2>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>

        {/* ⭐ The art gets its OWN cell between the title and the actions, so a
            crowded header crops it instead of letting it draw through the
            buttons. `basis-0 min-w-0` lets it collapse to nothing; it is
            hidden outright below lg, where there is no spare width at all. */}
        <div className="hidden lg:flex flex-1 basis-0 min-w-0 overflow-hidden justify-end text-primary/25">
          <LabTrace className="max-w-[420px]" />
        </div>

        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  )
}
